import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaFacebookF, FaLink, FaClock } from 'react-icons/fa';
import { apiConfig } from '../../lib/apiConfig';
import './PublicationDetail.css';

function decodeHtml(html) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function readingTime(content) {
  const text = (content || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const words = text.split(' ').filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function getDescription(pub) {
  const raw = pub.excerpt || pub.description || pub.body || '';
  return raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 155);
}

function ReadingProgressBar() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const scrolled = el.scrollTop || document.body.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      setProgress(total > 0 ? Math.min(100, (scrolled / total) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className="read-progress-wrap">
      <div className="read-progress-bar" style={{ width: `${progress}%` }} />
    </div>
  );
}

function ShareButtons({ title }) {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="share-bar">
      <span className="share-bar__label">Partager :</span>
      {/* <a
        href={`https://wa.me/?text=${encodeURIComponent(title + ' — ' + url)}`}
        target="_blank"
        rel="noreferrer"
        className="share-btn share-btn--wa"
        aria-label="Partager sur WhatsApp"
      >
        <FaWhatsapp size={15} /> WhatsApp
      </a> */}
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noreferrer"
        className="share-btn share-btn--fb"
        aria-label="Partager sur Facebook"
      >
        <FaFacebookF size={14} /> Facebook
      </a>
      <button
        type="button"
        className="share-btn share-btn--copy"
        onClick={copyLink}
        aria-label="Copier le lien"
      >
        <FaLink size={13} /> {copied ? 'Copie !' : 'Copier'}
      </button>
    </div>
  );
}

function SkeletonDetail() {
  return (
    <div className="pub-detail-skeleton">
      <div className="skel skel--title" />
      <div className="skel skel--meta" />
      <div className="skel skel--img" />
      <div className="skel skel--line" />
      <div className="skel skel--line" />
      <div className="skel skel--line skel--short" />
    </div>
  );
}

function RelatedArticles({ currentId, category }) {
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (!category) return;
    fetch(`${apiConfig.publicationBase}/publications?published=true&archived=false&sort=publishedAt&dir=desc`)
      .then(r => r.json())
      .then(data => {
        const all = (data?.items || data?.data || []);
        const filtered = all
          .filter(p => (p.id || p._id) !== currentId && p.category === category)
          .slice(0, 3);
        setRelated(filtered);
      })
      .catch(() => {});
  }, [currentId, category]);

  if (related.length === 0) return null;

  return (
    <div className="related-articles">
      <h3 className="related-articles__title">Dans la meme categorie</h3>
      <div className="related-articles__grid">
        {related.map(pub => {
          const id = pub.id || pub._id;
          return (
            <Link key={id} to={`/publications/${id}`} className="related-article-card">
              {pub.coverImage && (
                <img src={pub.coverImage} alt={pub.title} loading="lazy" className="related-article-card__img" />
              )}
              {!pub.coverImage && <div className="related-article-card__img-placeholder">NXS</div>}
              <div className="related-article-card__body">
                {pub.category && <span className="related-article-card__cat">{pub.category}</span>}
                <div className="related-article-card__title">{pub.title}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function PublicationDetail() {
  const { id } = useParams();
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPublication() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${apiConfig.publicationBase}/publications/${id}`);
        const data = await res.json();
        if (data && data.success && data.data) {
          setPublication(data.data);
        } else {
          setError('Publication introuvable');
        }
      } catch (e) {
        setError('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    }
    if (id) fetchPublication();
  }, [id]);

  const getBodyHtml = (pub) => {
    const raw = pub.body || pub.description || '';
    return /&lt;|&gt;|&amp;/.test(raw) ? decodeHtml(raw) : raw;
  };

  const mins = publication ? readingTime(publication.body || publication.description) : 0;

  return (
    <>
      <ReadingProgressBar />

      {publication && (
        <Helmet>
          <title>{publication.title} — NEXUS SOLUTIONS Blog</title>
          <meta name="description" content={getDescription(publication)} />
          <meta property="og:title" content={`${publication.title} — NEXUS SOLUTIONS`} />
          <meta property="og:description" content={getDescription(publication)} />
          {publication.coverImage && <meta property="og:image" content={publication.coverImage} />}
          <meta property="og:type" content="article" />
        </Helmet>
      )}

      <div className="pub-detail-page">
        <div className="pub-detail-container">
          <Link to="/publications" className="pub-back-btn">
            &larr; Retour au blog
          </Link>

          {loading && <SkeletonDetail />}
          {error && <div className="pub-detail-error">{error}</div>}

          {!loading && !error && publication && (
            <article className="pub-detail-article">
              <div className="pub-detail-meta">
                {publication.category && <span className="pub-detail-cat">{publication.category}</span>}
                {publication.publishedAt && <span className="pub-detail-date">{formatDate(publication.publishedAt)}</span>}
                <span className="pub-detail-rt"><FaClock size={11} /> {mins} min de lecture</span>
              </div>

              <h1 className="pub-detail-title">{publication.title}</h1>

              <ShareButtons title={publication.title} />

              {publication.coverImage && (
                <div className="pub-detail-cover">
                  <img src={publication.coverImage} alt={publication.title} loading="lazy" />
                </div>
              )}

              <div
                className="pub-detail-body"
                dangerouslySetInnerHTML={{ __html: getBodyHtml(publication) }}
              />

              <ShareButtons title={publication.title} />

              {publication.attachments && publication.attachments.length > 0 && (
                <div className="pub-detail-attachments">
                  <h4>Pieces jointes</h4>
                  <ul>
                    {publication.attachments.map((att, idx) => (
                      <li key={idx}>
                        <a href={att.dataUrl || att.url} target="_blank" rel="noreferrer">{att.name}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <RelatedArticles currentId={id} category={publication.category} />
            </article>
          )}

          {!loading && !error && publication && (
            <div className="pub-detail-cta">
              <div className="pub-detail-cta__inner">
                <h3>Vous avez un projet digital ?</h3>
                <p>Discutons-en. Obtenez une estimation gratuite en 4 questions.</p>
                <Link to="/devis" className="pub-detail-cta__btn">
                  Demarrer mon devis gratuit &rarr;
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
